import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";

// Volume cell in the intermediary Volumes table. View mode shows the value with
// a pencil; edit mode reveals the input plus save/cancel icons.
export default class extends Controller {
  connect() {
    this.boundCloseOnOutside = this.closeOnOutside.bind(this);
    document.addEventListener("click", this.boundCloseOnOutside, true);
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutside, true);
  }

  edit(event) {
    event.preventDefault();

    const cell = this._cellFor(event.target);
    this._closeOtherEdits(cell);
    this._openEdit(cell);
  }

  cancel(event) {
    event.preventDefault();
    this._resetCell(this._cellFor(event.target));
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.save(event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.cancel(event);
    }
  }

  async save(event) {
    event.preventDefault();

    const cell = this._cellFor(event.target);
    const input = this._inputFor(cell);
    const body = new FormData();
    body.append("intermediary_volume[volume]", input.value);
    input.dataset.state = "saving";

    try {
      const response = await httpRequest(input.dataset.url, { method: "PATCH", body });
      const data = await response.json();
      if (data.success) {
        input.dataset.state = "saved";
        input.defaultValue = input.value;
        this._updateDisplay(cell, input.value);
        this._closeEdit(cell);
      } else {
        input.dataset.state = "error";
      }
    } catch (_error) {
      input.dataset.state = "error";
    }
  }

  closeOnOutside(event) {
    const openCell = this.element.querySelector("[data-volume-cell][data-volume-editing]");
    if (!openCell || openCell.contains(event.target)) return;

    this._resetCell(openCell);
  }

  _cellFor(el) {
    return el.closest("[data-volume-cell]");
  }

  _inputFor(cell) {
    return cell.querySelector("[data-volume-input]");
  }

  _openEdit(cell) {
    cell.dataset.volumeEditing = "true";
    cell.querySelector("[data-volume-view]").classList.add("hidden");
    cell.querySelector("[data-volume-edit-button]").classList.add("hidden");

    const actions = cell.querySelector("[data-volume-edit-actions]");
    actions.classList.remove("hidden");
    actions.classList.add("inline-flex");

    const input = this._inputFor(cell);
    input.classList.remove("hidden");
    input.focus();
  }

  _closeEdit(cell) {
    delete cell.dataset.volumeEditing;
    cell.querySelector("[data-volume-view]").classList.remove("hidden");
    cell.querySelector("[data-volume-edit-button]").classList.remove("hidden");

    const actions = cell.querySelector("[data-volume-edit-actions]");
    actions.classList.add("hidden");
    actions.classList.remove("inline-flex");

    this._inputFor(cell).classList.add("hidden");
  }

  // Restore the input to its last-saved value and leave edit mode.
  _resetCell(cell) {
    if (!cell) return;

    const input = this._inputFor(cell);
    input.value = input.defaultValue;
    delete input.dataset.state;
    this._closeEdit(cell);
  }

  _closeOtherEdits(activeCell) {
    this.element.querySelectorAll("[data-volume-cell][data-volume-editing]").forEach((cell) => {
      if (cell !== activeCell) this._resetCell(cell);
    });
  }

  _updateDisplay(cell, rawValue) {
    const display = cell.querySelector("[data-volume-display]");
    display.textContent = this._formatVolume(rawValue);
  }

  _formatVolume(rawValue) {
    const trimmed = rawValue.toString().trim();
    if (!trimmed) return "N/A";

    const normalized = trimmed.replace(/,/g, "").replace(/\.0+$/, "");
    const num = Number(normalized);
    if (Number.isNaN(num)) return trimmed;

    return new Intl.NumberFormat().format(num);
  }
}
